import { Link, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useState, useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import MobileNav from './MobileNav';

const MatrixDownloadButton = ({ isGoingMerryPage }) => {
  const prefersReduced = useReducedMotion();
  const originalText = "Download";
  const [displayText, setDisplayText] = useState(originalText);
  const [isAnimating, setIsAnimating] = useState(false);

  // Apple Design: Scramble effect using requestAnimationFrame (interruptible)
  const handleMouseEnter = () => {
    if (prefersReduced) return;

    setIsAnimating(true);
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&";
    let frame = 0;
    const totalFrames = 20; // ~333ms at 60fps

    const animate = () => {
      if (!isAnimating) return;

      setDisplayText(
        originalText
          .split("")
          .map((char, index) => {
            if (index < frame) {
              return originalText[index];
            }
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join("")
      );

      frame++;
      if (frame <= totalFrames) {
        requestAnimationFrame(animate);
      } else {
        setDisplayText(originalText);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  };

  const handleMouseLeave = () => {
    setIsAnimating(false);
    setDisplayText(originalText);
  };

  // Adaptive colors based on page theme using spec files
  const btnBg = isGoingMerryPage ? 'bg-[#62B7FF]' : 'bg-[#e1dabe]';
  const btnHoverBg = isGoingMerryPage ? 'hover:bg-[#8FD4FF]' : 'hover:bg-[#fefdfe]';
  const btnText = isGoingMerryPage ? 'text-[#080B14]' : 'text-[#1e2541]';
  const btnShadow = isGoingMerryPage
    ? 'hover:shadow-[0_0_20px_rgba(98,183,255,0.4)]'
    : 'hover:shadow-[0_0_20px_rgba(225,218,190,0.4)]';

  return (
    <Link
      to="/download"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`px-6 py-2.5 rounded-full ${btnBg} ${btnText} ${btnHoverBg} ${btnShadow} font-bold text-xs uppercase tracking-widest hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] select-none text-center block`}
    >
      {displayText}
    </Link>
  );
};

export default function Header() {
  const location = useLocation();
  const isRamseyTheme = location.pathname === '/ramsey';
  const isGoingMerryPage = location.pathname === '/goingmerry';

  const { authStatus } = useAuthenticator(context => [context.user, context.authStatus]);
  const isAuthenticated = authStatus === 'authenticated';

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const updateBannerHeight = () => {
      const banner = document.getElementById("dopove-notice-banner");
      if (banner) {
        setBannerHeight(banner.offsetHeight);
      } else {
        setBannerHeight(0);
      }
    };

    updateBannerHeight();
    const t1 = setTimeout(updateBannerHeight, 50);
    const t2 = setTimeout(updateBannerHeight, 150);
    const t3 = setTimeout(updateBannerHeight, 400);

    window.addEventListener("resize", updateBannerHeight);
    const interval = setInterval(updateBannerHeight, 500);

    return () => {
      window.removeEventListener("resize", updateBannerHeight);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(interval);
    };
  }, []);

  // Compute navigation item colors dynamically from brand files
  const activeLinkColor = isGoingMerryPage ? 'text-[#62B7FF]' : 'text-[#e1dabe]';
  const hoverLinkColor = isGoingMerryPage ? 'hover:text-[#8FD4FF]' : 'hover:text-[#fefdfe]';
  const textPrimaryColor = isRamseyTheme ? 'text-[#323639]' : 'text-[#fefdfe]';
  const textSecondaryColor = isRamseyTheme ? 'text-[#323639]/70' : 'text-[#AAB4C4]';

  return (
    <>
      <header 
        style={{ top: scrolled ? 0 : `${bannerHeight}px` }}
        className={`fixed inset-x-0 z-[100] transition-[top,background-color,border-color,box-shadow] duration-500 border-b ${
          scrolled 
            ? isRamseyTheme
              ? 'bg-[#f4f0e9]/90 backdrop-blur-md border-[#d0ccc5]/50 py-4 shadow-md h-20'
              : isGoingMerryPage
                ? 'bg-[#080B14]/90 backdrop-blur-md border-white/5 py-4 shadow-lg h-20'
                : 'bg-[#1e2541]/90 backdrop-blur-md border-white/5 py-4 shadow-lg h-20'
            : isRamseyTheme
              ? 'bg-[#f4f0e9] border-[#d0ccc5]/50 py-5 h-22'
              : 'bg-transparent border-transparent py-6 h-24'
        }`}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-12">
          
          {/* Logo Branding */}
          <Link to="/" className="z-10 flex items-center gap-3" onClick={() => setIsMenuOpen(false)}>
            <img
              src="/logo_trans_1x1_white.png"
              alt="DOPOVE Logo"
              className={`h-9 w-auto drop-shadow-lg ${isRamseyTheme ? 'brightness-0 opacity-80' : 'opacity-100'}`}
            />
            <span className={`text-xl font-bold tracking-tight ${textPrimaryColor}`}>DOPOVE</span>
          </Link>
          
          {/* Mobile menu button */}
          <button 
            type="button"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            className="xl:hidden z-[110] p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className={isRamseyTheme ? 'text-[#323639]' : 'text-white'} size={28} />
            ) : (
              <Menu className={isRamseyTheme ? 'text-[#323639]' : 'text-white'} size={28} />
            )}
          </button>

          {/* Desktop Navigation Links & Actions */}
          <div className="hidden xl:flex items-center gap-8">
            <nav className={`flex items-center gap-8 font-semibold text-sm uppercase tracking-widest ${textSecondaryColor}`}>
              <Link
                to="/"
                className={`${location.pathname === '/' ? activeLinkColor : ''} ${hoverLinkColor} transition-[color] duration-200 ease-[var(--ease-out)]`}
              >
                Home
              </Link>
              <Link 
                to="/ice" 
                className={`${location.pathname === '/ice' ? activeLinkColor : ''} ${hoverLinkColor} transition-[color] duration-200 ease-[var(--ease-out)]`}
              >
                ICE
              </Link>
              <Link 
                to="/goingmerry" 
                className={`${location.pathname === '/goingmerry' ? activeLinkColor : ''} ${hoverLinkColor} transition-[color] duration-200 ease-[var(--ease-out)]`}
              >
                GoingMerry
              </Link>
              <a 
                href="https://docs.dopove.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className={`${hoverLinkColor} transition-[color] duration-200 ease-[var(--ease-out)]`}
              >
                Docs
              </a>
              <Link 
                to="/contact" 
                className={`${location.pathname === '/contact' ? activeLinkColor : ''} ${hoverLinkColor} transition-colors`}
              >
                About
              </Link>
              {isAuthenticated && (
                <Link 
                  to="/profile" 
                  className={`${location.pathname === '/profile' ? activeLinkColor : ''} ${hoverLinkColor} transition-colors`}
                >
                  Profile
                </Link>
              )}
            </nav>

            {/* CTA Buttons */}
            <div className="flex items-center gap-4">
              {!isAuthenticated && (
                <Link 
                  to="/auth?state=signIn" 
                  className={`px-5 py-2.5 rounded-full border hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)] text-xs uppercase tracking-widest font-bold ${
                    isRamseyTheme 
                      ? 'border-[#323639]/30 text-[#323639] hover:bg-[#323639]/10' 
                      : 'border-white/10 text-white hover:bg-white/5'
                  }`}
                >
                  Sign In
                </Link>
              )}
              <MatrixDownloadButton isGoingMerryPage={isGoingMerryPage} />
            </div>
          </div>
        </div>
      </header>
      <MobileNav isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
}
